import { useCallback, useEffect, useRef, useState } from "react";
import {
  createInvitation,
  listPendingInvitations,
  resendInvitation,
  revokeInvitation,
} from "../../services/invitations";
import type {
  Invitation,
  InvitationAdminFailureKind,
} from "../../types/invitations";
import type { AddUserSubmission } from "../admin/AddUserModal";

/** A line of feedback after an invitation action. */
export interface InvitationNotice {
  /**
   * Which invitation this line is about, as a lowercased email address.
   *
   * Keyed rather than kept in one slot because `busyIds` is a set: two rows really can be working
   * at once, and with a single slot the second to settle silently replaced the first's message. A
   * resend that failed with `send_failed` followed a moment later by a successful withdrawal of a
   * different row left the admin never told that the first email did not go out — and a
   * `role="alert"` swapped for a `role="status"` inside 200ms is not announced either.
   *
   * The address rather than the invitation id, because one row per address is already this list's
   * identity rule (see `upsert`). That makes the line about a person, so an invite and a later
   * resend of the same invitation replace each other rather than stacking up two lines about one
   * coach. The list read has no invitation of its own and uses {@link LIST_NOTICE_KEY}.
   */
  key: string;
  tone: "success" | "error";
  text: string;
}

/**
 * The one notice that is about the list itself rather than about any invitation on it.
 *
 * Cannot collide with a real key: those are email addresses, and every address carries an "@".
 */
const LIST_NOTICE_KEY = "(list)";

export interface OrganizationInvitations {
  invitations: Invitation[];
  loading: boolean;
  /**
   * False when this API has no invitations endpoints. Web and the API ship on separate workflows,
   * so a new build can meet an older server: the feature hides itself rather than filling the
   * users screen with errors about something the admin never asked for.
   */
  supported: boolean;
  /**
   * One line per invitation, least recently updated first, rather than a single slot two rows
   * would fight over.
   *
   * Every entry is still true when it is read: a refusal stands until that same invitation is
   * acted on again, so a later success elsewhere cannot quietly take it away.
   */
  notices: readonly InvitationNotice[];
  /**
   * Every invitation with an action in flight.
   *
   * A set rather than one id: resending row A and withdrawing row B is two requests, and a single
   * slot would re-enable A the moment B started, then clear B's marker when A settled. Both rows
   * would read as idle with two requests outstanding, one click from a double submit.
   */
  busyIds: ReadonlySet<string>;
  inviting: boolean;
  inviteError: string | null;
  clearInviteError: () => void;
  /** Resolves true when the dialog should close, false when it should stay open with the error. */
  invite: (submission: AddUserSubmission) => Promise<boolean>;
  resend: (invitation: Invitation) => Promise<void>;
  revoke: (invitation: Invitation) => Promise<void>;
}

/** What a refused call carried, read structurally so a mocked service still narrows correctly. */
interface Failure {
  kind: InvitationAdminFailureKind;
  message: string;
  invitation: Invitation | null;
}

const failureOf = (error: unknown): Failure => {
  const thrown = (error ?? {}) as {
    kind?: unknown;
    message?: unknown;
    invitation?: unknown;
  };

  return {
    kind:
      typeof thrown.kind === "string"
        ? (thrown.kind as InvitationAdminFailureKind)
        : "failed",
    message: typeof thrown.message === "string" ? thrown.message : "",
    invitation: (thrown.invitation as Invitation | null) ?? null,
  };
};

/**
 * The api client falls back to "API request failed: 409 Conflict" when a body carried no message.
 * That is a developer's string, not something to put in front of a coach.
 */
const serverMessage = (message: string): string | null =>
  message && !message.startsWith("API request failed") ? message : null;

type Copy = Partial<Record<InvitationAdminFailureKind, string>>;

/**
 * Copy per refusal, written here rather than taken from the server.
 *
 * The throttle and the missing transport get their own sentences on purpose. Both refuse again if
 * the admin simply retries, so "something went wrong" would send them round the same loop with no
 * idea why. Kinds with no entry fall through to the server's own message, which is the one that
 * knows what a bad address or an unmapped failure actually was.
 */
const INVITE_COPY: Copy = {
  conflict:
    "That address already has an account in your organization, so there is nothing to send. You will find them in the users list.",
  throttled:
    "An invitation went to that address within the last hour. You can send another one a little later.",
  no_transport:
    "Invitations cannot be sent right now because email is not set up. Nothing was sent, so try again later or contact support.",
  unsupported:
    "Invitations are not available right now. Try again in a few minutes.",
  not_found: "That invitation is no longer there.",
};

const RESEND_COPY: Copy = {
  conflict:
    "That invitation is no longer pending, so it cannot be sent again. The list has been refreshed.",
  throttled:
    "That invitation went out within the last hour. You can send it again a little later.",
  no_transport:
    "The invitation could not be sent because email is not set up right now. Try again later or contact support.",
  send_failed: "The email did not go out. Try sending it again in a moment.",
  not_found: "That invitation is no longer there.",
  unsupported: "Invitations are not available right now.",
};

const REVOKE_COPY: Copy = {
  conflict:
    "That invitation is no longer pending. The list has been refreshed.",
  not_found: "That invitation is no longer there.",
  unsupported: "Invitations are not available right now.",
};

const messageFor = (copy: Copy, failure: Failure): string =>
  copy[failure.kind] ??
  serverMessage(failure.message) ??
  "Something went wrong. Please try again.";

/** One line per address, matching the one-row-per-address rule `upsert` keeps below. */
const noticeKeyFor = (email: string): string => email.toLowerCase();

/** Newest first, and one row per address: a second invite re-arms the row rather than adding one. */
const upsert = (list: Invitation[], row: Invitation): Invitation[] => [
  row,
  ...list.filter(
    (i) =>
      i.id !== row.id && i.email.toLowerCase() !== row.email.toLowerCase()
  ),
];

/**
 * The organization's pending invitations, and the three things an admin does with them.
 *
 * Lives beside the users table rather than inside it: the table already had enough to say, and
 * pulling this out keeps the invitation states (in flight, refused, throttled, saved but not sent)
 * in one place where they can be read together.
 */
export const useOrganizationInvitations = (): OrganizationInvitations => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [supported, setSupported] = useState(true);
  const [notices, setNotices] = useState<readonly InvitationNotice[]>([]);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  /**
   * Which read is allowed to write, and what has happened locally since it went out.
   *
   * A read racing a *write* is the case that matters here. Withdrawing B succeeds and filters it
   * out, a 409 on A calls `refresh()`, and that GET was issued before B's DELETE committed — so
   * taking its answer whole put B back on screen with a live Resend button, directly underneath a
   * notice saying it had just been withdrawn. Pressing Resend then 409s and refreshes again.
   */
  const readSeqRef = useRef(0);
  const editsSinceReadRef = useRef<Array<(list: Invitation[]) => Invitation[]>>([]);

  /** Says one thing about one invitation, replacing only what was said about that same one. */
  const say = useCallback(
    (key: string, tone: InvitationNotice["tone"], text: string) => {
      setNotices((current) => [
        ...current.filter((n) => n.key !== key),
        { key, tone, text },
      ]);
    },
    []
  );

  /**
   * Drops one invitation's line as its next action starts.
   *
   * Its own and no other: clearing the lot here is what let a row that was merely *starting* work
   * silence the result another row had already finished reporting.
   */
  const clearNotice = useCallback((key: string) => {
    setNotices((current) => current.filter((n) => n.key !== key));
  }, []);

  /**
   * A local edit to the list, applied and recorded.
   *
   * Recorded because a read already in flight is about to answer with a snapshot taken before this
   * edit existed, and replaying it onto that snapshot is what stops the two undoing each other.
   */
  const editList = useCallback((update: (list: Invitation[]) => Invitation[]) => {
    editsSinceReadRef.current.push(update);
    setInvitations(update);
  }, []);

  /**
   * Re-reads the list, then folds back in whatever happened locally while the request was out.
   *
   * The server's answer is the base, not the last word. Discarding a raced read looks safer and
   * breaks the other half of this: `refresh()` is the only thing that drops a row the server has
   * told us is gone, and the copy beside it promises exactly that ("The list has been refreshed").
   * Throwing the answer away leaves the refused row on screen with a live Resend, underneath a
   * notice saying it is no longer pending — the same defect from the other side.
   *
   * A newer read still wins outright, since it was issued later and its own snapshot already
   * contains everything this one would have replayed.
   */
  const readList = useCallback(async () => {
    const seq = ++readSeqRef.current;
    editsSinceReadRef.current = [];

    const rows = await listPendingInvitations();
    if (readSeqRef.current !== seq) return;

    const since = editsSinceReadRef.current;
    editsSinceReadRef.current = [];
    setInvitations(since.reduce((list, edit) => edit(list), rows));
    // The list is in front of us, so anything we said about failing to load it has expired.
    clearNotice(LIST_NOTICE_KEY);
  }, [clearNotice]);

  useEffect(() => {
    let cancelled = false;

    readList()
      .catch((error: unknown) => {
        if (cancelled) return;
        if (failureOf(error).kind === "unsupported") {
          setSupported(false);
          return;
        }
        // Reported, but only about itself. The users table beside it loaded fine and must not be
        // taken down by a list of invitations failing to arrive.
        say(LIST_NOTICE_KEY, "error", "Pending invitations could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      // Nothing is waiting for this read any more. React 19 dropped the warning for a state write
      // after unmount, so it now fails silently; useGetStartedChecklist keeps the same rule.
      readSeqRef.current += 1;
    };
  }, [readList, say]);

  const markBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  /** Re-reads the list after the server has told us our copy of it is out of date. */
  const refresh = useCallback(async () => {
    try {
      await readList();
    } catch {
      // The notice that sent us here already says what happened; a second one about the refresh
      // would replace the reason with a symptom.
    }
  }, [readList]);

  const invite = useCallback(
    async (submission: AddUserSubmission): Promise<boolean> => {
      const key = noticeKeyFor(submission.email);
      setInviting(true);
      setInviteError(null);
      clearNotice(key);

      try {
        // The dialog does not collect names on this path and the API has nowhere to put them, so
        // only the two fields an invitation actually carries are passed on.
        const created = await createInvitation({
          email: submission.email,
          role: submission.role,
        });

        editList((list) => upsert(list, created));
        say(
          key,
          "success",
          `Invitation sent to ${created.email}. The link works for 7 days.`
        );
        return true;
      } catch (error: unknown) {
        const failure = failureOf(error);

        /*
          The row was saved and the email was not. Closing is right: the invitation exists, it is
          in the table below with a Resend beside it, and keeping the dialog open would invite a
          second attempt that creates nothing and says the same thing.
        */
        const saved = failure.invitation;
        if (failure.kind === "send_failed") {
          if (saved) {
            editList((list) => upsert(list, saved));
          } else {
            void refresh();
          }
          say(
            key,
            "error",
            `The invitation for ${submission.email} was saved, but the email did not go out. Use Resend below to try again.`
          );
          return true;
        }

        setInviteError(messageFor(INVITE_COPY, failure));
        return false;
      } finally {
        setInviting(false);
      }
    },
    [clearNotice, editList, refresh, say]
  );

  const resend = useCallback(
    async (invitation: Invitation) => {
      const key = noticeKeyFor(invitation.email);
      markBusy(invitation.id, true);
      clearNotice(key);

      try {
        const sent = await resendInvitation(invitation.id);
        editList((list) => upsert(list, sent));
        say(
          key,
          "success",
          `Invitation sent to ${sent.email} again. The new link works for 7 days and the old one no longer does.`
        );
      } catch (error: unknown) {
        const failure = failureOf(error);
        say(key, "error", messageFor(RESEND_COPY, failure));

        /*
          A 502 hands back the row it saved, and that row is the only thing carrying "this person
          received nothing". Dropping it leaves the table showing a delivery that did not happen.
        */
        const saved = failure.invitation;
        if (failure.kind === "send_failed" && saved) {
          editList((list) => upsert(list, saved));
        }

        if (failure.kind === "conflict" || failure.kind === "not_found") {
          await refresh();
        }
      } finally {
        markBusy(invitation.id, false);
      }
    },
    [clearNotice, editList, markBusy, refresh, say]
  );

  const revoke = useCallback(
    async (invitation: Invitation) => {
      const key = noticeKeyFor(invitation.email);
      markBusy(invitation.id, true);
      clearNotice(key);

      try {
        await revokeInvitation(invitation.id);
        editList((list) => list.filter((i) => i.id !== invitation.id));
        say(
          key,
          "success",
          `The invitation for ${invitation.email} was withdrawn. Their link no longer works.`
        );
      } catch (error: unknown) {
        const failure = failureOf(error);
        say(key, "error", messageFor(REVOKE_COPY, failure));

        if (failure.kind === "conflict" || failure.kind === "not_found") {
          await refresh();
        }
      } finally {
        markBusy(invitation.id, false);
      }
    },
    [clearNotice, editList, markBusy, refresh, say]
  );

  return {
    invitations,
    loading,
    supported,
    notices,
    busyIds,
    inviting,
    inviteError,
    clearInviteError: () => setInviteError(null),
    invite,
    resend,
    revoke,
  };
};
